import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Edit2, Trash2, Home, Building, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';

const CustomerAddresses = () => {
    const { addresses, addAddress, updateAddress, removeAddress } = useUser();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        label: 'Home'
    });

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let success = false;
        
        if (editId) {
            success = await updateAddress(editId, formData);
        } else {
            success = await addAddress(formData);
        }
        
        if (success) {
            closeForm();
        }
    };

    const handleEdit = (address) => {
        setFormData({
            firstName: address.firstName || '',
            lastName: address.lastName || '',
            phone: address.phone || '',
            street: address.street || '',
            city: address.city || '',
            state: address.state || '',
            zip: address.zip || '',
            label: address.label || 'Home'
        });
        setEditId(address._id);
        setIsFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this address?")) {
            await removeAddress(id);
        }
    };

    const closeForm = () => {
        setIsFormOpen(false);
        setEditId(null);
        setFormData({
            firstName: '',
            lastName: '',
            phone: '',
            street: '',
            city: '',
            state: '',
            zip: '',
            label: 'Home'
        });
    };

    return (
        <div className="customer-addresses">
            {/* Header */}
            <div className="addresses-header">
                <div>
                    <h2>Saved Addresses</h2>
                    <p>Manage your delivery locations</p>
                </div>
                {!isFormOpen && (
                    <button onClick={() => setIsFormOpen(true)} className="add-btn">
                        <Plus size={18} /> Add Address
                    </button>
                )}
            </div>

            {/* Add/Edit Form */}
            {isFormOpen && (
                <div className="address-form">
                    <h3>{editId ? 'Edit Address' : 'Add New Address'}</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-field">
                                <label>First Name</label>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="John" required />
                            </div>
                            <div className="form-field">
                                <label>Last Name</label>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Doe" required />
                            </div>
                        </div>

                        <div className="form-field full">
                            <label>Phone Number</label>
                            <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="9876543210" required />
                        </div>

                        <div className="form-field full">
                            <label>Address Line</label>
                            <input type="text" name="street" value={formData.street} onChange={handleInputChange} placeholder="House No, Building, Street" required />
                        </div>

                        <div className="form-row">
                            <div className="form-field">
                                <label>City</label>
                                <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" required />
                            </div>
                            <div className="form-field">
                                <label>State</label>
                                <input type="text" name="state" value={formData.state} onChange={handleInputChange} placeholder="State" required />
                            </div>
                            <div className="form-field">
                                <label>Pincode</label>
                                <input type="text" name="zip" value={formData.zip} onChange={handleInputChange} placeholder="110001" required />
                            </div>
                        </div>

                        <div className="form-field">
                            <label>Address Type</label>
                            <div className="type-buttons">
                                <button type="button" className={`type-btn ${formData.label === 'Home' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, label: 'Home' })}>
                                    <Home size={16} /> Home
                                </button>
                                <button type="button" className={`type-btn ${formData.label === 'Work' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, label: 'Work' })}>
                                    <Building size={16} /> Work
                                </button>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="save-btn">Save Address</button>
                            <button type="button" onClick={closeForm} className="cancel-btn">Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Addresses List */}
            {!isFormOpen && addresses.length === 0 && (
                <div className="empty-addresses">
                    <MapPin size={48} />
                    <h3>No addresses saved</h3>
                    <p>Add your first delivery address</p>
                    <button onClick={() => setIsFormOpen(true)} className="add-btn-outline">
                        Add New Address
                    </button>
                </div>
            )}

            {!isFormOpen && addresses.length > 0 && (
                <div className="addresses-grid">
                    {addresses.map((address) => (
                        <div key={address._id} className="address-card">
                            <div className="card-header">
                                <div className="type-badge">
                                    {address.label === 'Home' ? <Home size={14} /> : <Building size={14} />}
                                    {address.label}
                                </div>
                                <div className="card-actions">
                                    <button onClick={() => handleEdit(address)} className="action-btn edit" title="Edit">
                                        <Edit2 size={14} />
                                    </button>
                                    <button onClick={() => handleDelete(address._id)} className="action-btn delete" title="Delete">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="card-body">
                                <h4>{address.firstName} {address.lastName}</h4>
                                <p>{address.street}</p>
                                <p>{address.city}, {address.state} - {address.zip}</p>
                                <div className="phone-info">
                                    <span>📞 {address.phone}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <style>{`
                .customer-addresses {
                    max-width: 100%;
                }

                /* Header */
                .addresses-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .addresses-header h2 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 4px;
                }

                .addresses-header p {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0;
                }

                .add-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 20px;
                    background: #10b981;
                    border: none;
                    border-radius: 40px;
                    color: white;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .add-btn:hover {
                    background: #059669;
                }

                /* Form */
                .address-form {
                    background: white;
                    border: 1px solid #e8ecef;
                    border-radius: 16px;
                    padding: 24px;
                    margin-bottom: 24px;
                }

                .address-form h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 20px;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                    margin-bottom: 16px;
                }

                .form-field {
                    margin-bottom: 16px;
                }

                .form-field.full {
                    grid-column: span 2;
                }

                .form-field label {
                    display: block;
                    font-size: 12px;
                    font-weight: 500;
                    color: #1e293b;
                    margin-bottom: 6px;
                }

                .form-field input {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 13px;
                    transition: all 0.2s;
                }

                .form-field input:focus {
                    outline: none;
                    border-color: #10b981;
                }

                .type-buttons {
                    display: flex;
                    gap: 12px;
                }

                .type-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 20px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 40px;
                    font-size: 13px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .type-btn.active {
                    background: #10b981;
                    border-color: #10b981;
                    color: white;
                }

                .form-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 20px;
                    padding-top: 16px;
                    border-top: 1px solid #e8ecef;
                }

                .save-btn {
                    padding: 10px 24px;
                    background: #10b981;
                    border: none;
                    border-radius: 40px;
                    color: white;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                }

                .cancel-btn {
                    padding: 10px 24px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 40px;
                    color: #64748b;
                    font-size: 13px;
                    cursor: pointer;
                }

                /* Addresses Grid */
                .addresses-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }

                .address-card {
                    background: white;
                    border: 1px solid #e8ecef;
                    border-radius: 16px;
                    overflow: hidden;
                    transition: all 0.2s;
                }

                .address-card:hover {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 16px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e8ecef;
                }

                .type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 10px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 500;
                    color: #64748b;
                }

                .card-actions {
                    display: flex;
                    gap: 8px;
                }

                .action-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .action-btn.edit {
                    color: #64748b;
                }

                .action-btn.edit:hover {
                    color: #10b981;
                }

                .action-btn.delete {
                    color: #64748b;
                }

                .action-btn.delete:hover {
                    color: #ef4444;
                }

                .card-body {
                    padding: 16px;
                }

                .card-body h4 {
                    font-size: 15px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 8px;
                }

                .card-body p {
                    font-size: 12px;
                    color: #64748b;
                    margin: 0 0 4px;
                    line-height: 1.4;
                }

                .phone-info {
                    margin-top: 12px;
                    padding-top: 10px;
                    border-top: 1px solid #e8ecef;
                    font-size: 12px;
                    color: #64748b;
                }

                /* Empty State */
                .empty-addresses {
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e8ecef;
                }

                .empty-addresses svg {
                    color: #94a3b8;
                    margin-bottom: 16px;
                }

                .empty-addresses h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 6px;
                }

                .empty-addresses p {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0 0 20px;
                }

                .add-btn-outline {
                    padding: 10px 24px;
                    background: white;
                    border: 1px solid #10b981;
                    border-radius: 40px;
                    color: #10b981;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .addresses-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .form-row {
                        grid-template-columns: 1fr;
                    }
                    
                    .form-field.full {
                        grid-column: span 1;
                    }
                    
                    .form-actions {
                        flex-direction: column;
                    }
                    
                    .save-btn, .cancel-btn {
                        text-align: center;
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default CustomerAddresses;